<template>
  <div class="response-time-chart">
    <svg
      :width="width"
      :height="height"
      viewBox="0 0 800 80"
      class="w-full h-full"
    >
      <!-- Grid lines -->
      <g class="grid">
        <line
          v-for="y in gridLines"
          :key="`grid-y-${y}`"
          :x1="0"
          :y1="y"
          :x2="800"
          :y2="y"
          stroke="#E5E7EB"
          stroke-width="0.5"
          opacity="0.5"
        />
      </g>

      <!-- Y-axis labels -->
      <g class="y-labels">
        <text
          v-for="label in yAxisLabels"
          :key="`ylabel-${label.value}`"
          :x="-5"
          :y="label.y"
          text-anchor="end"
          dominant-baseline="middle"
          class="text-xs fill-gray-500"
        >
          {{ label.text }}
        </text>
      </g>

      <!-- Data line -->
      <path
        :d="linePath"
        fill="none"
        :stroke="primaryColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />

      <!-- Data points -->
      <circle
        v-for="(point, index) in dataPoints"
        :key="`point-${index}`"
        :cx="point.x"
        :cy="point.y"
        r="3"
        :fill="primaryColor"
        class="hover:stroke-2 hover:stroke-white"
        @mouseenter="showTooltip($event, point)"
        @mouseleave="hideTooltip"
      />

      <!-- Area under the line (optional subtle fill) -->
      <path
        :d="areaPath"
        :fill="primaryColor"
        fill-opacity="0.1"
        stroke="none"
      />

    </svg>

    <!-- Tooltip -->
    <div
      v-if="tooltip.show"
      :style="{
        left: tooltip.x + 'px',
        top: tooltip.y + 'px'
      }"
      class="fixed z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none"
    >
      {{ tooltip.content }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

interface DataPoint {
  date: string
  responseTime: number
}

interface ChartPoint {
  x: number
  y: number
  date: string
  responseTime: number
}

const props = defineProps<{
  data: DataPoint[]
  width: number
  height: number
  primaryColor: string
}>()

const tooltip = ref({
  show: false,
  x: 0,
  y: 0,
  content: ''
})

// Chart dimensions
const chartWidth = 800
const chartHeight = 80
const padding = { top: 10, right: 10, bottom: 10, left: 40 }

// Calculate scales
const xScale = computed(() => {
  const dataLength = props.data.length
  return dataLength > 1 ? (chartWidth - padding.left - padding.right) / (dataLength - 1) : 0
})

const yScale = computed(() => {
  const maxResponseTime = Math.max(...props.data.map(d => d.responseTime), 1000)
  const minResponseTime = Math.min(...props.data.map(d => d.responseTime), 0)
  const range = maxResponseTime - minResponseTime || 1
  return (chartHeight - padding.top - padding.bottom) / range
})

const yOffset = computed(() => {
  const minResponseTime = Math.min(...props.data.map(d => d.responseTime), 0)
  return minResponseTime
})

// Transform data to chart coordinates
const dataPoints = computed((): ChartPoint[] => {
  return props.data.map((point, index) => ({
    x: padding.left + index * xScale.value,
    y: chartHeight - padding.bottom - ((point.responseTime - yOffset.value) * yScale.value),
    date: point.date,
    responseTime: point.responseTime
  }))
})

// Generate SVG path for the line
const linePath = computed(() => {
  if (dataPoints.value.length === 0) return ''

  const points = dataPoints.value
  let path = `M ${points[0].x} ${points[0].y}`

  for (let i = 1; i < points.length; i++) {
    path += ` L ${points[i].x} ${points[i].y}`
  }

  return path
})

// Generate SVG path for the area under the line
const areaPath = computed(() => {
  if (dataPoints.value.length === 0) return ''

  const points = dataPoints.value
  let path = `M ${points[0].x} ${chartHeight - padding.bottom}`

  // Line to first point
  path += ` L ${points[0].x} ${points[0].y}`

  // Connect all points
  for (let i = 1; i < points.length; i++) {
    path += ` L ${points[i].x} ${points[i].y}`
  }

  // Close the area back to bottom
  path += ` L ${points[points.length - 1].x} ${chartHeight - padding.bottom} Z`

  return path
})

// Grid lines
const gridLines = computed(() => {
  const lines = []
  const step = (chartHeight - padding.top - padding.bottom) / 4

  for (let i = 0; i <= 4; i++) {
    lines.push(padding.top + i * step)
  }

  return lines
})

// Y-axis labels
const yAxisLabels = computed(() => {
  const maxResponseTime = Math.max(...props.data.map(d => d.responseTime), 1000)
  const minResponseTime = Math.min(...props.data.map(d => d.responseTime), 0)
  const range = maxResponseTime - minResponseTime || 1

  const labels = []
  for (let i = 0; i <= 4; i++) {
    const value = minResponseTime + (range * i / 4)
    const y = chartHeight - padding.bottom - (i * (chartHeight - padding.top - padding.bottom) / 4)
    labels.push({
      value,
      y,
      text: formatResponseTime(value)
    })
  }

  return labels
})

function formatResponseTime(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`
  }
  return `${Math.round(ms)}ms`
}

function showTooltip(event: MouseEvent, point: ChartPoint) {
  const rect = (event.target as Element).getBoundingClientRect()
  const containerRect = (event.currentTarget as Element).closest('.response-time-chart-container')?.getBoundingClientRect()

  if (containerRect) {
    tooltip.value = {
      show: true,
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.top - containerRect.top - 35,
      content: `${point.date}: ${formatResponseTime(point.responseTime)}`
    }
  }
}

function hideTooltip() {
  tooltip.value.show = false
}

// Hide tooltip when component unmounts
onUnmounted(() => {
  tooltip.value.show = false
})
</script>

<style scoped>
.response-time-chart {
  position: relative;
}

.response-time-chart circle:hover {
  r: 4;
  stroke: white;
  stroke-width: 2;
}
</style>